import dayjs from "dayjs";
import { Mui } from "@leap4/frontend";
import { numericFontFamily } from "../../../theme/colors";
import { PredictionErrorBand } from "../reporting/reports";
import { formatProjected, IEventSummary, IProjectionWindow } from "./summarize";

const { Box, Chip, Typography, ButtonBase, Divider } = Mui;

export const PROJECTION_COLORS = { counted: "#6366F1", expected: "#A5B4FC", forecast: "#8B5CF6" };

// Describes the spread of likely outcomes, which is what the percentage measures, rather than a grade.
const BAND_TEXT: Record<PredictionErrorBand, string> = {
    good: "Narrow range",
    warn: "Moderate range",
    bad: "Wide range",
};

// Low is amber, not red: a wide range is a caution, not a failure.
const bandChipSx = (band: PredictionErrorBand) => ({
    fontWeight: 600,
    bgcolor: (t: any) =>
        t.palette.mode === "dark"
            ? { good: "#1C3327", warn: "#2A2950", bad: "#3A2E12" }[band]
            : { good: "#E6F5EC", warn: "#EEEEFC", bad: "#FDF1D8" }[band],
    color: (t: any) =>
        t.palette.mode === "dark"
            ? { good: "#A6E3C0", warn: "#C9C8FF", bad: "#F2C66B" }[band]
            : { good: "#1F6B41", warn: "#4342B8", bad: "#8A5A00" }[band],
});

const hatch = (color: string) => `repeating-linear-gradient(45deg, ${color} 0 4px, transparent 4px 8px)`;

const CompositionBar = ({ s }: { s: IEventSummary }) => {
    const total = s.total || 1;
    const pct = (v: number) => `${Math.max(0, (v / total) * 100)}%`;
    return (
        <Box
            role="img"
            aria-label={`Of ${formatProjected(s.total)}: ${formatProjected(s.counted)} counted, ${formatProjected(s.stillToLand)} still to arrive, ${formatProjected(s.forecastTotal)} forecast`}
            sx={{ display: "flex", height: 8, borderRadius: 999, overflow: "hidden", bgcolor: "action.hover", my: 1.5 }}
        >
            <Box sx={{ width: pct(s.counted), bgcolor: PROJECTION_COLORS.counted }} />
            <Box sx={{ width: pct(s.stillToLand), background: hatch(PROJECTION_COLORS.counted), opacity: 0.7 }} />
            <Box sx={{ width: pct(s.forecastTotal), bgcolor: PROJECTION_COLORS.forecast, opacity: 0.35 }} />
        </Box>
    );
};

const BreakdownRow = ({ label, value, strong }: { label: string; value: number; strong?: boolean }) => (
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 2, py: 0.25 }}>
        <Typography component="dt" variant="caption" color="text.secondary">
            {label}
        </Typography>
        <Typography component="dd" sx={{ m: 0, fontFamily: numericFontFamily, fontVariantNumeric: "tabular-nums", fontWeight: strong ? 700 : 600, fontSize: strong ? "0.875rem" : "0.75rem" }}>
            {formatProjected(value)}
        </Typography>
    </Box>
);

const Breakdown = ({ s, win }: { s: IEventSummary; win: IProjectionWindow }) => (
    <Box component="dl" sx={{ m: 0 }}>
        <BreakdownRow label="Counted so far" value={s.counted} />
        <BreakdownRow label="Still to arrive from days already run" value={s.stillToLand} />
        <BreakdownRow label={`Forecast, next ${s.forecastDays} days`} value={s.forecastTotal} />
        <Divider sx={{ my: 1 }} />
        <BreakdownRow label={`By end of ${dayjs(win.monthTo).format("MMMM")} (${dayjs(win.monthFrom).format("MMM D")} – ${dayjs(win.monthTo).format("MMM D")})`} value={s.monthTotal} strong />
    </Box>
);

interface IProjectionEventCardProps {
    summary: IEventSummary;
    win: IProjectionWindow;
    layout: "single" | "full" | "compact";
    selected: boolean;
    selectable: boolean;
    onSelect: () => void;
}

export const ProjectionEventCard = ({ summary: s, win, layout, selected, selectable, onSelect }: IProjectionEventCardProps) => {
    const range = s.errorPct !== null ? [s.total * (1 - s.errorPct / 100), s.total * (1 + s.errorPct / 100)] : null;
    const showBreakdown = layout !== "compact" || selected;

    const headline = (
        <Box sx={{ minWidth: 0 }}>
            <Typography
                title={s.label}
                sx={{ fontSize: "0.75rem", color: "text.secondary", mb: 0.75, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", wordBreak: "break-word" }}
            >
                Attributed {s.label} · {dayjs(win.from).format("MMM D")} – {dayjs(win.to).format("MMM D")}
            </Typography>
            <Typography sx={{ fontFamily: numericFontFamily, fontVariantNumeric: "tabular-nums", fontSize: layout === "single" ? "2.75rem" : layout === "full" ? "1.875rem" : "1.5rem", fontWeight: 600, letterSpacing: "-0.03em", lineHeight: 1.05 }}>
                {formatProjected(s.total)}
            </Typography>
            {s.band && range && (
                <Box sx={{ mt: 1.25 }}>
                    <Chip size="small" label={`${BAND_TEXT[s.band]} · ±${s.errorPct!.toFixed(0)}%`} sx={bandChipSx(s.band)} />
                    <Typography variant="body2" sx={{ mt: 0.75 }}>
                        The final total will most likely land between <strong>{formatProjected(range[0])}</strong> and <strong>{formatProjected(range[1])}</strong>, within {s.errorPct!.toFixed(0)}% of this estimate either way.
                    </Typography>
                </Box>
            )}
            <CompositionBar s={s} />
            {s.band && s.band !== "good" && (
                <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                    {Math.round(s.uncountedShare * 100)}% of this total is still settling or forecast. The range narrows as those days are counted.
                </Typography>
            )}
        </Box>
    );

    const body =
        layout === "single" ? (
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) minmax(260px, 360px)" }, gap: { xs: 2, md: 4 }, alignItems: "start" }}>
                {headline}
                <Breakdown s={s} win={win} />
            </Box>
        ) : (
            <>
                {headline}
                {showBreakdown && <Breakdown s={s} win={win} />}
            </>
        );

    const cardSx = {
        display: "block",
        width: "100%",
        textAlign: "left",
        bgcolor: "background.paper",
        border: 1,
        borderColor: selected && selectable ? "primary.main" : "divider",
        boxShadow: selected && selectable ? (t: any) => `0 0 0 1px ${t.palette.primary.main}` : "none",
        borderRadius: "12px",
        p: layout === "single" ? 3 : "16px 18px",
        minWidth: 0,
    };

    // The card is the chart's event picker only when there is more than one event to pick from.
    return selectable ? (
        <ButtonBase component="div" aria-pressed={selected} aria-label={`Show ${s.label} in chart`} onClick={onSelect}
            sx={{ ...cardSx, "&.Mui-focusVisible": { outline: "2px solid", outlineColor: "primary.main", outlineOffset: 2 } }}>
            {body}
        </ButtonBase>
    ) : (
        <Box sx={cardSx}>{body}</Box>
    );
};
